/* eslint-disable max-lines */
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { createTheme, type Themes } from '@this/constants/themes';
import type User from '@this/domain/user/user';
import type { ReceiptCreationActionCreateResponse } from '@this/src/types/receipt_creation_action';
import Notification from '@this/src/notification';
import { Fetcher } from '@this/src/util';
import { Input } from '@this/components/shared/ui/inputs/input';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import type { AITTheme } from '@this/shared/ui/theme';

import { getBaseReceiptFormStyles } from './receipt_form_styles';

const API_PATHS = {
  NEW: '/trip_receipts/new.json',
  CREATE: '/trip_receipts'
};

const REDIRECT_PATH = '/trip_receipts';

const MESSAGES = {
  ISSUE_SUCCESS: '領収書発行を開始しました。処理完了まで時間がかかる場合があります。',
  COMMUNICATION_ERROR: '通信環境が不安定です。\n時間をおいてもう一度お試しください。',
  EMAIL_REQUIRED: 'メールアドレスを入力してください。',
  INVALID_TRIP_IDS: '有効な旅程IDがありません。領収書発行が可能な旅程を選択してください。'
};

const FORM = {
  DEFAULT_PRICE_REMARKS_TYPE: 'travel_expenses',
  PRICE_REMARKS_TYPES: {
    TRAVEL: 'travel_expenses',
    SALES: 'sales_expenses'
  },
  PLACEHOLDERS: {
    EMAIL: 'メールアドレスを入力してください',
    NAME: '宛名を入力してください'
  },
  BACK_LINK_TEXT: '< 戻る',
  SUBMIT_BUTTON_TEXT: '領収書を発行する'
};

const UI_TEXT = {
  TITLE: '領収書発行',
  TRIP_ID: '旅程番号',
  DESTINATION: '送信先',
  EMAIL: 'メールアドレス',
  RECEIPT_INFO: '領収書情報',
  NAME: '宛名',
  PRICE_BREAKDOWN: '料金の内訳',
  REMARKS: '但書',
  BREAKDOWN_OPTIONS: {
    NONE: 'なし',
    INCLUDE: 'あり'
  },
  REMARKS_OPTIONS: {
    TRAVEL: '出張代金として',
    SALES: '営業交通費として'
  }
};

interface Props {
  user: User | null;
  tripReceiptAvailable: boolean;
}

type Response = {
  trip_ids: number[];
};

const TripReceiptForm: React.FC<Props> = ({ user, tripReceiptAvailable }) => {
  const location = useLocation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const isProcessingRef = useRef(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const rawParamValue = searchParams.get('trip_ids');
  const rawTripIds = rawParamValue?.split(',').filter(id => id.trim() !== '') || [];
  const [validTripIds, setValidTripIds] = useState<string[]>([]);
  const processedIdsRef = useRef<Set<string>>(new Set());
  const [hasValidTripIds, setHasValidTripIds] = useState(validTripIds.length > 0);
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState(user?.organization?.name || '');
  const [includePriceBreakdown, setIncludePriceBreakdown] = useState(false);
  const [priceRemarksType, setPriceRemarksType] = useState(FORM.DEFAULT_PRICE_REMARKS_TYPE);
  const [error, setError] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const isMountedRef = useRef(true);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setProcessingState = (state: boolean) => {
    setIsProcessing(state);
    isProcessingRef.current = state;
  };

  useEffect(() => {
    setHasValidTripIds(validTripIds.length > 0);
  }, [validTripIds]);

  useEffect(() => {
    if (rawTripIds.length === 0) {
      setIsLoading(false);
      setIsInitialLoadComplete(true);
      return;
    }

    const unprocessedTripIds = rawTripIds.filter(id => !processedIdsRef.current.has(id));

    if (unprocessedTripIds.length === 0) {
      setIsLoading(false);
      setIsInitialLoadComplete(true);
      return;
    }

    Fetcher.get<Response>(API_PATHS.NEW, { trip_ids: unprocessedTripIds })
      .then(res => {
        if (res && res.trip_ids) {
          setValidTripIds(res.trip_ids.map(id => String(id)));
        }
        unprocessedTripIds.forEach(id => processedIdsRef.current.add(id));
      })
      .catch(e => {
        if (isMountedRef.current) {
          const errors =
            e.response.data && e.response.data.errors ? e.response.data.errors : [MESSAGES.COMMUNICATION_ERROR];

          unprocessedTripIds.forEach(id => processedIdsRef.current.add(id));

          setHasValidTripIds(false);

          setTimeout(() => {
            if (isMountedRef.current) {
              Notification.error(errors);
            }
          }, 0);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
          setIsInitialLoadComplete(true);
        }
      });
  }, [tripReceiptAvailable, rawTripIds]);

  const theme = createTheme(1);
  const styles = getStyles(isLoading, theme, isMobile);

  const handleSubmit = () => {
    if (isProcessingRef.current) {
      return;
    }

    setError('');

    if (isInitialLoadComplete && !hasValidTripIds) {
      Notification.error(MESSAGES.INVALID_TRIP_IDS);
      return;
    }

    if (!email.trim()) {
      setError(MESSAGES.EMAIL_REQUIRED);
      return;
    }

    setIsLoading(true);
    setProcessingState(true);
    const params = {
      trip_ids: validTripIds,
      email,
      name,
      include_price_breakdown: includePriceBreakdown,
      price_remarks_type: priceRemarksType
    };

    Fetcher.post<ReceiptCreationActionCreateResponse>(API_PATHS.CREATE, params)
      .then((response: ReceiptCreationActionCreateResponse) => {
        if (!isMountedRef.current) return;

        if (!response) {
          throw new Error(MESSAGES.COMMUNICATION_ERROR);
        }

        Notification.success(MESSAGES.ISSUE_SUCCESS);
        setProcessingState(false);
        history.push(REDIRECT_PATH);
      })
      .catch(e => {
        if (!isMountedRef.current) return;

        const errors =
          e.response?.data?.error ||
          (e.response?.data?.errors ? e.response.data.errors : [MESSAGES.COMMUNICATION_ERROR]);
        Notification.error(errors);
        setProcessingState(false);
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>{UI_TEXT.TITLE}</div>
      {isLoading && (
        <div style={styles.loadingOverlay}>
          <Loading loading />
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.subTitle}>{UI_TEXT.TRIP_ID}</div>
        <div style={styles.childContent}>
          {hasValidTripIds ? (
            <div style={styles.tripIds}>{validTripIds.join(', ')}</div>
          ) : (
            <div style={styles.errorText}>{MESSAGES.INVALID_TRIP_IDS}</div>
          )}
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.subTitle}>{UI_TEXT.DESTINATION}</div>
        <div style={styles.childContent}>
          <div style={styles.label}>
            {UI_TEXT.EMAIL} <span style={styles.required}>*</span>
          </div>
          <div>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={FORM.PLACEHOLDERS.EMAIL}
              size="medium"
              style={{ width: '400px', maxWidth: '100%' }}
            />
            {error && <p style={styles.errorText}>{error}</p>}
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.subTitle}>{UI_TEXT.RECEIPT_INFO}</div>
        <div style={styles.childContent}>
          <div style={styles.label}>{UI_TEXT.NAME}</div>
          <Input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={FORM.PLACEHOLDERS.NAME}
            size="medium"
            style={{ width: '400px', maxWidth: '100%' }}
          />
        </div>

        <div style={styles.childContent}>
          <div style={styles.label}>{UI_TEXT.PRICE_BREAKDOWN}</div>
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                checked={!includePriceBreakdown}
                onChange={() => setIncludePriceBreakdown(false)}
              />
              {UI_TEXT.BREAKDOWN_OPTIONS.NONE}
            </label>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                checked={includePriceBreakdown}
                onChange={() => setIncludePriceBreakdown(true)}
              />
              {UI_TEXT.BREAKDOWN_OPTIONS.INCLUDE}
            </label>
          </div>
        </div>

        <div style={styles.childContent}>
          <div style={styles.label}>{UI_TEXT.REMARKS}</div>
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                checked={priceRemarksType === FORM.PRICE_REMARKS_TYPES.TRAVEL}
                onChange={() => setPriceRemarksType(FORM.PRICE_REMARKS_TYPES.TRAVEL)}
              />
              {UI_TEXT.REMARKS_OPTIONS.TRAVEL}
            </label>
            <label style={styles.radioLabel}>
              <input
                type="radio"
                checked={priceRemarksType === FORM.PRICE_REMARKS_TYPES.SALES}
                onChange={() => setPriceRemarksType(FORM.PRICE_REMARKS_TYPES.SALES)}
              />
              {UI_TEXT.REMARKS_OPTIONS.SALES}
            </label>
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.childContent}>
          <p style={styles.noticeText}>
            領収書発行後の返金処理はいたしかねます。
            <br />
            減泊処理やキャンセル予定がある場合は、領収書発行前に必ずお申し出ください。
            <br />
            旅程の訂正方法は、
            <a
              href="/knowledge_categories/1"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: theme.linkColor }}
            >
              FAQ
            </a>
            にてご案内しております。
          </p>
        </div>
        <div style={styles.childContent}>
          <div style={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={e => setIsConfirmed(e.target.checked)}
              id="confirmCheckbox"
            />
            <label htmlFor="confirmCheckbox" style={styles.checkText}>
              減泊や返金が発生しないことを確認しました。
            </label>
          </div>
        </div>
      </div>

      <div style={styles.buttonContainer}>
        <a onClick={() => history.goBack()} style={styles.confirmBack}>
          {FORM.BACK_LINK_TEXT}
        </a>
        <Button
          color="primary"
          size="medium"
          loading={isLoading}
          disabled={isLoading || !hasValidTripIds || isProcessing || !isConfirmed}
          onClick={handleSubmit}
        >
          {FORM.SUBMIT_BUTTON_TEXT}
        </Button>
      </div>
    </div>
  );
};

function getStyles(isLoading: boolean, theme: Themes & AITTheme, isMobile: boolean) {
  const base = getBaseReceiptFormStyles(isLoading, theme);

  return {
    ...base,
    childContent: {
      ...base.childContent,
      flexDirection: isMobile ? ('column' as const) : ('row' as const)
    } as React.CSSProperties,

    label: {
      ...base.label,
      width: isMobile ? '100%' : '200px'
    } as React.CSSProperties,

    tripIds: {
      fontSize: '13px',
      wordWrap: 'break-word'
    } as React.CSSProperties,

    radioGroup: {
      display: 'flex',
      gap: '10px',
      fontSize: '13px'
    } as React.CSSProperties,

    radioLabel: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '2px'
    } as React.CSSProperties
  };
}

export default TripReceiptForm;
